import { T } from '@tolgee/react';
import { Collapse } from 'antd';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { useFaqItems } from './faqItems';

export type FaqVariant = 'light' | 'green';

type FaqSectionProps = {
  /**
   * `green` is the long-standing full-bleed green band used on /quotes.
   * `light` is the pricing page design: white cards on beige.
   */
  variant?: FaqVariant;
};

export const FaqSection = ({ variant = 'green' }: FaqSectionProps) => {
  const items = useFaqItems();

  const title = (
    <T keyName="Frequently asked questions" defaultValue="Frequently asked questions" />
  );

  if (variant === 'light') {
    return (
      <LightSection id="faq">
        <LightInner>
          <LightTitle>{title}</LightTitle>
          <LightList>
            {items.map(item => (
              <LightCard key={item.key}>
                <LightSummary>
                  <span>{item.label}</span>
                  <Plus aria-hidden="true">+</Plus>
                </LightSummary>
                <LightAnswer>{item.content}</LightAnswer>
              </LightCard>
            ))}
          </LightList>
        </LightInner>
      </LightSection>
    );
  }

  return (
    <GreenWrapper>
      <CenteredSection>
        <GreenTitle>{title}</GreenTitle>
        <FAQCollapse accordion>
          {items.map(item => (
            <Collapse.Panel header={item.label} key={item.key}>
              {item.content}
            </Collapse.Panel>
          ))}
        </FAQCollapse>
      </CenteredSection>
    </GreenWrapper>
  );
};

/* ─── Light variant (pricing page design) ─────────────────────────────────── */

const LightSection = styled.section`
  width: 100%;
  background: ${theme.colors.landingPageBeige};
  padding: 84px 32px 96px;
  overflow: hidden;

  @media (max-width: 992px) {
    padding: 60px 20px 72px;
  }
`;

const LightInner = styled.div`
  max-width: 860px;
  margin: 0 auto;
`;

const LightTitle = styled.h2`
  font-size: clamp(32px, 3.6vw, 44px);
  font-weight: 900;
  letter-spacing: -0.02em;
  text-align: center;
  margin: 0 0 36px;
  color: ${theme.colors.black};
`;

const LightList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Plus = styled.span`
  font-size: 22px;
  color: ${theme.colors.orange};
  transition: transform 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  flex-shrink: 0;
  line-height: 1;
`;

const LightSummary = styled.summary`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  font-size: 17px;
  font-weight: 900;
  color: ${theme.colors.black};
  cursor: pointer;

  /* Hide the native disclosure triangle in favour of the orange plus. */
  list-style: none;
  &::-webkit-details-marker {
    display: none;
  }

  @media (max-width: 600px) {
    font-size: 16px;
  }
`;

const LightAnswer = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 1.55;
  color: rgba(40, 47, 51, 0.68);

  p {
    margin: 14px 0 0;
  }

  ul {
    margin: 14px 0 0;
    padding-left: 20px;
  }

  a {
    font-weight: 800;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const LightCard = styled.details`
  background: ${theme.colors.white};
  border-radius: 10px;
  padding: 20px 24px;
  box-shadow: 0 2px 20px 0 #deaf8d33;

  /*
   * Don't rely on the UA stylesheet's "details:not([open]) > *:not(summary) {
   * display: none }" rule to collapse the answer — it's easily lost to a
   * higher-priority author rule (any explicit "display" on the answer, from
   * this file or a global reset) since author styles beat user-agent styles
   * regardless of specificity. Own the toggle explicitly instead, keyed off
   * the same [open] attribute the browser already manages on click.
   */
  & > ${LightAnswer} {
    display: none;
  }

  &[open] > ${LightAnswer} {
    display: block;
  }

  &[open] ${Plus} {
    transform: rotate(45deg);
  }

  @media (max-width: 600px) {
    padding: 16px 18px;
  }
`;

/* ─── Green variant (unchanged /quotes treatment) ─────────────────────────── */

const GreenWrapper = styled.div`
  background: ${theme.colors.green};
  width: 100%;
  padding: 1px 0px;
`;

const CenteredSection = styled.div`
  width: 100%;
  max-width: 1128px;
  margin: 40px auto 30px;
  display: flex;
  flex-direction: column;

  @media (max-width: 1128px) {
    max-width: 100%;
    padding: 0px 20px !important;
  }

  @media (max-width: 500px) {
    padding: 5px !important;
  }
`;

const GreenTitle = styled.h2`
  font-weight: bold;
  width: 100%;
  text-align: center;
  font-size: 36px;
  color: ${theme.colors.white};

  @media (max-width: 900px) {
    font-size: 24px;
  }
`;

const FAQCollapse = styled(Collapse)`
  font-size: 18px;
  font-family: 'Nunito';
  border: none;
  padding: 20px;
  background: ${theme.colors.primary};

  p {
    margin: 0;
  }

  a {
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
  svg {
    width: 18px;
    height: 18px;
  }
  .ant-collapse-item .ant-collapse-header {
    color: ${theme.colors.white};
    font-weight: bold;
    font-size: 20px;
  }

  .ant-collapse-item {
    border: none;
    border-bottom: 1px solid #ffffff90;
    border-radius: 0px !important;
  }
  .ant-collapse-item.ant-collapse-item.ant-collapse-item-active {
    border-bottom: none;
  }

  .ant-collapse-content {
    border: none;
    border-left: 4px solid ${theme.colors.orange};
    border-radius: 10px;
  }

  .ant-collapse-item:last-child > .ant-collapse-content {
    border-radius: 10px;
  }
`;

export default FaqSection;
